import React, { FC, useEffect, useState, useRef } from 'react';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import TopBlock from './TopBlock';
import { RootStoreType } from 'src/types/store';
import { allColumns, REQUIRED_MESSAGES_ON_BOX_GRID_COLUMNS } from './constants';
import Table from 'src/components/Table';
import {
    TablePropsOptionsType,
    OnChangeOptionsType,
    ExportTypeType,
    TableRefType,
    GetAllPrintRowsType,
} from 'src/types/table';
import { useDebounce } from 'src/utils/debounce';
import { editUserSettingsFetch } from 'src/resources/userSettings/reducer';
import { UserSettingsType } from 'src/types/userSettings';
import filterUnique from 'src/utils/filterUnique';
import api from '@api';
import { OrderExpeditionPackageType } from 'src/types/orderExpeditionPackage';
import { useCheckPermission } from 'src/utils/checkPermission';
import { getOrderMessagesOnExpeditionBoxGridFetch } from 'src/resources/orderMessagesOnExpeditionBoxGrid/reducer';
import { ORDER_MESSAGE_ON_EXPEDITION_BOX_GRID_URL } from 'src/resources/orderMessagesOnExpeditionBoxGrid/sagas';
import { countryOptionsCodes } from 'src/utils/countryOptions';
import ChromeReaderModeIcon from '@mui/icons-material/ChromeReaderMode';
import { ShippingLabelIcon } from 'src/components/SkladonIcon/SmallIcons';

export const SESSION_NAME = 'orderMessagesOnExpeditionBox';

const initTableOptions: TablePropsOptionsType = {
    filters: [],
    sort: { column: 'ID', direction: 'DESC' },
    page: 1,
    items: 20,
    columns: [],
};

const OrderMessagesOnExpeditionBoxContainer: FC = () => {
    const dispatch = useDispatch();
    const tableRef = useRef<TableRefType>(null);

    const sessionOptions = JSON.parse(sessionStorage.getItem(SESSION_NAME) || 'null');

    const [formatedData, setFormatedData] = useState<OrderExpeditionPackageType[]>([]);
    const [tableOptions, setTableOptions] = useState<TablePropsOptionsType>(sessionOptions || initTableOptions);

    const loading = useSelector((state: RootStoreType) => state.orderMessagesOnExpeditionBoxGrid.loading);
    const orderMessagesOnExpeditionBoxData = useSelector(
        (state: RootStoreType) => state.orderMessagesOnExpeditionBoxGrid.data
    );

    const userSettingsData = useSelector((state: RootStoreType) => state.userSettings.data);

    const permissionDetail = useCheckPermission('MESSAGES_ON_EXPEDITION_BOX.DETAIL');
    const permissionCreate = useCheckPermission('MESSAGES_ON_EXPEDITION_BOX.CREATE');

    useEffect(() => {
        dispatch(setLocationParams({ name: translate('orderMessagesOnExpeditionBox.title') }));
    }, []);

    const debouncedTableOptions = useDebounce(tableOptions, 500);

    useEffect(() => {
        loadTableData();
    }, [debouncedTableOptions]);

    useEffect(() => {
        if (orderMessagesOnExpeditionBoxData?.data) {
            setFormatedData(getFormatedData(orderMessagesOnExpeditionBoxData?.data || []));
        }
    }, [orderMessagesOnExpeditionBoxData?.data]);

    const getFormatedData = (data: OrderExpeditionPackageType[]): OrderExpeditionPackageType[] => {
        return data;
    };

    const getAllGridParams = () => ({
        filters: [[...(tableOptions.filters || [])]],
        sorts: [tableOptions.sort],
        page: tableOptions.page,
        items: tableOptions.items,
        columns: [
            ...REQUIRED_MESSAGES_ON_BOX_GRID_COLUMNS,
            ...tableOptions.columns.filter((column) => !column.allwaysShow).map((column) => column.key),
        ].filter(filterUnique),
    });

    const loadTableData = () => {
        if (tableOptions.columns.length) {
            dispatch(getOrderMessagesOnExpeditionBoxGridFetch({ params: getAllGridParams() }));
        }
    };

    const handleChangeOptions: OnChangeOptionsType = (newOptions) => {
        setTableOptions(newOptions);
    };

    const getAllRows: GetAllPrintRowsType = async (extendParams) => {
        const params = getAllGridParams();
        const res = await api('post', ORDER_MESSAGE_ON_EXPEDITION_BOX_GRID_URL, {
            ...params,
            ...extendParams,
        });
        return getFormatedData(res.data.data.data);
    };

    const handleExport = (type: ExportTypeType) => {
        tableRef.current?.exportTable(
            type,
            translate('orderMessagesOnExpeditionBox.action.exportFileName'),
            getAllRows
        );
    };

    const editUserSettings = (newUserSettings: UserSettingsType) => {
        dispatch(editUserSettingsFetch(newUserSettings));
    };

    const reloadTableData = () => {
        dispatch(getOrderMessagesOnExpeditionBoxGridFetch(undefined));
    };

    return (
        <>
            <TopBlock
                handleExport={handleExport}
                reloadTableData={reloadTableData}
                permissionCreate={permissionCreate}
            />
            <Table
                allColumns={allColumns}
                ref={tableRef}
                rowIdKey="ID"
                rows={formatedData}
                loading={loading}
                columsLabelPrefix="orderMessagesOnExpeditionBox.table"
                options={tableOptions}
                totalCount={orderMessagesOnExpeditionBoxData?.totalCount}
                onChangeOptions={handleChangeOptions}
                rowActions={[
                    {
                        // icon: <ChromeReaderModeIcon />,
                        icon: <ShippingLabelIcon />,
                        label: translate('orderMessagesOnExpeditionBox.action.messageOnExpeditionBoxDetail'),
                        getLink: (row) => `/orders/messages_on_expedition_box/${row.ID}/`,
                        hiddenFce: () => !permissionDetail,
                    },
                ]}
                selectOptions={{ countryOptionsCodes }}
                userSettingsName="showMessagesOnExpeditionBoxGridColumns"
                userSettingsData={userSettingsData}
                editUserSettings={editUserSettings}
                optionsSessionName={SESSION_NAME}
                heightVariant="top-tab"
                fullHeight
            />
        </>
    );
};

export default OrderMessagesOnExpeditionBoxContainer;
